import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/ReplayCircleFilled";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import axios from "axios";

export default function PaymentsOverviewPage() {
  // top cards
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    successCount: 0,
    refundedAmount: 0,
    failedCount: 0,
  });

  // last 10
  const [recent, setRecent] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1) dashboard summary
        const summaryRes = await axios.get("/api/admin/payments/summary", {
          headers: getAuthHeaders(),
        });

        // 2) last 10 successful/created payments
        const recentRes = await axios.get("/api/admin/payments/recent", {
          headers: getAuthHeaders(),
        });

        setSummary(summaryRes.data);
        setRecent(recentRes.data || []);
      } catch (err) {
        console.error("Dashboard load error:", err);
      }
    };

    fetchData();
  }, [getAuthHeaders]);

  const cards = [
    {
      label: "Toplam Gelir",
      value: `₺${summary.totalRevenue?.toLocaleString("tr-TR") || "0"}`,
      icon: <PaidIcon fontSize="large" />,
      bg: "linear-gradient(135deg, rgba(0,200,83,0.12), rgba(0,200,83,0.03))",
    },
    {
      label: "Başarılı Ödeme",
      value: summary.successCount ?? 0,
      icon: <CheckCircleIcon fontSize="large" />,
      bg: "linear-gradient(135deg, rgba(33,150,243,0.12), rgba(33,150,243,0.03))",
    },
    {
      label: "İade Edilen",
      value: `₺${summary.refundedAmount?.toLocaleString("tr-TR") || "0"}`,
      icon: <ReplayIcon fontSize="large" />,
      bg: "linear-gradient(135deg, rgba(255,193,7,0.12), rgba(255,193,7,0.03))",
    },
    {
      label: "Başarısız İşlem",
      value: summary.failedCount ?? 0,
      icon: <ErrorIcon fontSize="large" />,
      bg: "linear-gradient(135deg, rgba(244,67,54,0.12), rgba(244,67,54,0.03))",
    },
  ];

  return (
    <Box p={3} display="grid" gap={3}>
      {/* Üst metin */}
      <Box>
        <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
          <AccessTimeIcon fontSize="small" />
          Son 24 saat / Son 30 gün
        </Typography>
      </Box>

      {/* Kartlar */}
      <Grid container spacing={2}>
        {cards.map((c, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card
              elevation={4}
              sx={{
                background: c.bg,
                borderRadius: 3,
                height: "100%",
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {c.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {c.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: "rgba(0,0,0,0.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {c.icon}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Son Satın Almalar */}
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Son 10 Paket Alımı
          </Typography>

          {recent.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Son işlem bulunamadı.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {recent.map((p, idx) => (
                <React.Fragment key={p._id || idx}>
                  <Box
                    display="grid"
                    gridTemplateColumns={{
                      xs: "1fr",
                      sm: "2fr 2fr 1fr 1fr 1fr",
                    }}
                    rowGap={0.5}
                  >
                    {/* Kullanıcı */}
                    <Box>
                      <Typography fontWeight={600} variant="body1">
                        {p.user?.name || p.user?.email || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.user?.email}
                      </Typography>
                    </Box>

                    {/* Paket */}
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {p.package?.name || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{p.refCode || p._id?.slice(-6)}
                      </Typography>
                    </Box>

                    {/* Tutar */}
                    <Box>
                      <Typography fontWeight={600}>
                        ₺{(p.amount ?? 0).toLocaleString("tr-TR")}
                      </Typography>
                      {p.priceBefore && p.priceAfter && p.priceBefore > p.priceAfter && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textDecoration: "line-through" }}
                        >
                          ₺{p.priceBefore.toLocaleString("tr-TR")}
                        </Typography>
                      )}
                    </Box>

                    {/* Durum */}
                    <Box>
                      <Chip
                        size="small"
                        label={mapStatus(p.status)}
                        color={statusColor(p.status)}
                        variant="outlined"
                      />
                    </Box>

                    {/* Tarih */}
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(p.paidAt || p.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.method?.toUpperCase() || "-"}
                      </Typography>
                    </Box>
                  </Box>

                  {idx < recent.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

// helpers
function formatDate(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleString("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapStatus(s) {
  switch (s) {
    case "success":
    case "manual_approved":
      return "Ödendi";
    case "pending":
    case "manual_waiting":
      return "Beklemede";
    case "failed":
    case "manual_rejected":
      return "Hata";
    case "refunded":
      return "İade";
    default:
      return s;
  }
}

function statusColor(s) {
  if (s === "success" || s === "manual_approved") return "success";
  if (s === "failed" || s === "manual_rejected") return "error";
  if (s === "refunded") return "warning";
  return "info";
}